import { createFileRoute, Outlet, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { useEffect, useRef } from 'react'
import { AccountClosedGate } from '~/components/auth/account-closed-gate'
import { NewAccountEffects } from '~/components/auth/new-account-effects'
import { AppUpdateToast } from '~/components/layout/app-update-toast'
import { ImpersonationFrame } from '~/components/layout/impersonation-frame'
import { OnboardingSetupBootstrap } from '~/components/onboarding/setup/setup-bootstrap'
import { hasPendingOrStoredAuth } from '~/lib/auth-hint'
import { useIdentitySwitchTeardown, useImpersonationSession } from '~/lib/impersonation'
import { consumeSignOutInProgress } from '~/lib/router-auth'
import { useSignOutTeardown } from '~/lib/use-sign-out-teardown'

export const Route = createFileRoute('/_authenticated')({
  // Optimistic, like the `/` landing gate: a stored JWT means a signed-in (or
  // recently signed-in) user, so the shell renders straight away instead of
  // blocking on the Convex handshake — measured at ~820ms, over half of a
  // cold load, during which the screen can show nothing at all. A fresh OAuth
  // callback is allowed through too: its `?code=` must reach the mounted auth
  // provider before a JWT can exist.
  //
  // This is a HINT, not a check (see `hasPendingOrStoredAuth`). Rendering is
  // all it buys: nothing reads data on the strength of it, because every
  // query still needs a token the server verified, and the workspace layout
  // holds its own query until `isAuthenticated` is true. A stale token
  // therefore renders empty chrome and nothing more, until the verification
  // below lands and bounces it to `/sign-in`.
  beforeLoad: ({ location }) => {
    if (hasPendingOrStoredAuth()) {
      return
    }
    // No token at all — signed out for certain, without asking the server.
    const isSignOut = consumeSignOutInProgress()
    throw redirect({
      search: isSignOut ? {} : { redirect: location.pathname },
      to: '/sign-in',
    })
  },
  component: AuthenticatedGate,
  // Client-only: everything behind auth needs no SSR.
  ssr: false,
})

/**
 * The auth boundary only. The app shell lives one level down in the
 * `$workspaceSlug` layout; the headless bootstraps stay here because they
 * must run even while the `/home` resolver decides which workspace to open —
 * a fresh sign-up seeds its starter studio from that in-between state.
 *
 * It also owns the sign-out teardown, because it is the component that can
 * unmount every authed subscriber at once. The account menu only raises the
 * latch (`beginSignOut`); the ordered teardown happens here.
 */
function AuthenticatedGate() {
  // Owns the authed subtree, so it owns the sign-out teardown — the hook does
  // the work and reports whether one is under way. Shared with the sign-up
  // route, which holds a new account through the onboarding questions and
  // therefore owns a subtree of its own.
  const signingOut = useSignOutTeardown()
  const switchingIdentity = useIdentitySwitchTeardown()
  const navigate = useNavigate()
  const redirected = useRef(false)
  const { isImpersonating, isResolved } = useImpersonationSession()
  const allowAutomaticEffects = isResolved && !isImpersonating
  const auth = useConvexAuth()
  const router = useRouter()

  // The other half of the optimistic gate above: `beforeLoad` let this tree
  // render on the strength of a stored token, and this is where the server's
  // verdict is enforced. An expired or revoked token settles here as
  // `isAuthenticated: false` and the session ends.
  //
  // Deliberately NOT driven off the token disappearing from storage: whether
  // `@convex-dev/auth` clears it on a failed refresh is an implementation
  // detail, and if it ever stopped, the user would sit on empty chrome with
  // no redirect. The auth state is the contract.
  //
  // Sign-out and identity switches settle unauthenticated too, and both own
  // an ordered teardown that ends in their own navigation — this must not
  // race them to a different destination.
  // The path is read imperatively, at the moment of the redirect, rather than
  // subscribed to: `useLocation()` updates the instant the navigation starts,
  // so a re-render would re-enter this and redirect again carrying
  // `redirect=/sign-in`. The latch makes it fire exactly once regardless.
  useEffect(() => {
    if (
      auth.isLoading ||
      auth.isAuthenticated ||
      signingOut ||
      switchingIdentity ||
      redirected.current
    ) {
      return
    }
    // Latched like the teardown below: the redirect fires exactly once.
    redirected.current = true
    void navigate({
      replace: true,
      search: { redirect: router.state.location.pathname },
      to: '/sign-in',
    })
  }, [auth.isLoading, auth.isAuthenticated, signingOut, switchingIdentity, navigate, router])

  // Same teardown ordering as sign-out, for the impersonation start/stop
  // boundary: the tree must come down before the cache clears and the hard
  // navigation fires — see `useIdentitySwitchTeardown`.
  if (signingOut || switchingIdentity) {
    return null
  }

  return (
    <ImpersonationFrame>
      <Outlet />
      {/* These fire mutations on mount with no user action. Under staff
          impersonation that would consume the TARGET's one-shot state —
          seeding their starter studio, firing a real Ads conversion under
          their email from this browser. The server no-ops them too; not
          mounting avoids the pointless round trip.
          Gated on `isResolved` so they don't fire for one tick on every load. */}
      {allowAutomaticEffects ? (
        <>
          <AccountClosedGate />
          <OnboardingSetupBootstrap />
          <NewAccountEffects />
        </>
      ) : null}
      {/* Identity-independent: staff still need to know their tab is stale. */}
      <AppUpdateToast />
    </ImpersonationFrame>
  )
}
